import { computeRcdFloatingPosition, parseRcdCssLength } from "../floating";

const setupMarker = Symbol.for("rcd.tooltip.setup");
const generatedContentByTrigger = new WeakMap();
let tooltipSequence = 0;
const parseCssLength = parseRcdCssLength;

export const computeRcdTooltipPosition = ({
    triggerRect,
    tooltipRect,
    ...options
}) => computeRcdFloatingPosition({
    anchorRect: triggerRect,
    surfaceRect: tooltipRect,
    ...options
});
const resolveTrigger = (target) => {
    if (target instanceof Element) {
        return target.matches("[data-rcd-tooltip-trigger]")
            ? target
            : target.closest("[data-rcd-tooltip-trigger]");
    }

    if (typeof target !== "string") {
        return null;
    }

    try {
        return document.querySelector(target);
    } catch {
        return null;
    }
};

const prepareTooltipTriggers = (root = document) => {
    const candidates = [];
    if (root instanceof Element && root.matches("[data-rcd-tooltip-text]")) candidates.push(root);
    root.querySelectorAll?.("[data-rcd-tooltip-text]").forEach((element) => candidates.push(element));
    candidates.forEach((trigger) => {
        trigger.setAttribute("data-rcd-tooltip-trigger", "");
        const isControl = trigger.matches("button, a[href], input, select, textarea, summary");
        const hasVisibleText = trigger.textContent?.trim();
        if (isControl && !hasVisibleText && !trigger.hasAttribute("aria-label") && !trigger.hasAttribute("aria-labelledby")) {
            trigger.setAttribute("aria-label", trigger.dataset.rcdTooltipText);
        }
    });
};

const restoreAttribute = (element, name, value) => {
    if (value === null) {
        element.removeAttribute(name);
        return;
    }

    element.setAttribute(name, value);
};

export const setupRcdTooltips = () => {
    if (window[setupMarker]) {
        return window.rcdTooltip;
    }

    window[setupMarker] = true;

    prepareTooltipTriggers();

    let active = null;
    let pendingOpen = null;
    let closeTimer = 0;
    let positionFrame = 0;
    const hoverQuery = window.matchMedia("(hover: hover)");

    const clearPendingOpen = () => {
        if (!pendingOpen) {
            return;
        }

        window.clearTimeout(pendingOpen.timer);
        pendingOpen = null;
    };

    const clearPendingClose = () => {
        if (!closeTimer) {
            return;
        }

        window.clearTimeout(closeTimer);
        closeTimer = 0;
    };

    const getDelay = (wrapper, name, fallback) => {
        const value = Number(wrapper.dataset[name]);
        return Number.isFinite(value) && value >= 0 ? value : fallback;
    };

    const syncPortalContext = (record) => {
        const themeHost = record.wrapper.closest("[data-theme]");
        const theme = themeHost?.getAttribute("data-theme")
            || document.documentElement.getAttribute("data-theme");
        const direction = getComputedStyle(record.wrapper).direction === "rtl" ? "rtl" : "ltr";

        if (theme) {
            record.content.setAttribute("data-theme", theme);
        }
        record.content.setAttribute("dir", direction);
        record.direction = direction;
    };

    const positionActive = () => {
        positionFrame = 0;
        if (!active) {
            return;
        }

        if (!active.trigger.isConnected || !active.wrapper.isConnected || !active.content.isConnected) {
            window.rcdTooltip.close("disconnected");
            return;
        }

        syncPortalContext(active);
        const triggerRect = active.trigger.getBoundingClientRect();
        const tooltipRect = active.content.getBoundingClientRect();
        const rootStyle = getComputedStyle(document.documentElement);
        const gap = parseCssLength(rootStyle.getPropertyValue("--rcd-space-2"), 8);
        const padding = parseCssLength(rootStyle.getPropertyValue("--rcd-space-3"), 12);
        const visualViewport = window.visualViewport;
        const viewport = {
            left: visualViewport?.offsetLeft || 0,
            top: visualViewport?.offsetTop || 0,
            width: visualViewport?.width || window.innerWidth,
            height: visualViewport?.height || window.innerHeight
        };
        const result = computeRcdTooltipPosition({
            triggerRect,
            tooltipRect,
            placement: active.wrapper.dataset.rcdTooltipPlacement,
            direction: active.direction,
            viewport,
            padding,
            gap
        });

        active.content.style.left = `${result.left}px`;
        active.content.style.top = `${result.top}px`;
        active.content.style.setProperty("--rcd-tooltip-arrow-left", `${result.arrowLeft}px`);
        active.content.style.setProperty("--rcd-tooltip-arrow-top", `${result.arrowTop}px`);
        active.content.dataset.rcdTooltipSide = result.side;
        active.content.dataset.rcdTooltipPlacement = result.placement;
    };

    const schedulePosition = () => {
        if (!active || positionFrame) {
            return;
        }

        positionFrame = window.requestAnimationFrame(positionActive);
    };

    const closeTooltip = (reason = "dismiss") => {
        clearPendingOpen();
        clearPendingClose();

        if (!active) {
            return false;
        }

        const record = active;
        active = null;
        if (positionFrame) {
            window.cancelAnimationFrame(positionFrame);
            positionFrame = 0;
        }

        record.wrapper.removeAttribute("data-state");
        record.content.removeAttribute("data-state");
        record.content.removeAttribute("data-rcd-tooltip-portal");
        record.content.removeAttribute("data-rcd-tooltip-side");
        record.content.removeAttribute("data-rcd-tooltip-placement");
        record.content.style.removeProperty("left");
        record.content.style.removeProperty("top");
        record.content.style.removeProperty("--rcd-tooltip-arrow-left");
        record.content.style.removeProperty("--rcd-tooltip-arrow-top");
        restoreAttribute(record.content, "data-theme", record.originalTheme);
        restoreAttribute(record.content, "dir", record.originalDirection);
        restoreAttribute(record.trigger, "aria-describedby", record.originalDescribedBy);

        if (record.parent.isConnected) {
            const insertionPoint = record.nextSibling?.parentNode === record.parent
                ? record.nextSibling
                : null;
            record.parent.insertBefore(record.content, insertionPoint);
        } else {
            record.content.remove();
            if (record.generated) generatedContentByTrigger.delete(record.trigger);
        }

        record.wrapper.dispatchEvent(new CustomEvent("rcd:tooltip:close", {
            bubbles: true,
            detail: { reason }
        }));
        return true;
    };

    const openTooltip = (target, activationReasons = new Set(["api"])) => {
        const trigger = resolveTrigger(target);
        const wrapper = trigger?.closest("[data-rcd-tooltip]") || trigger;
        let content = wrapper?.querySelector?.("[data-rcd-tooltip-content]") || generatedContentByTrigger.get(trigger);
        let generated = false;
        if (trigger instanceof HTMLElement && !(content instanceof HTMLElement) && trigger.dataset.rcdTooltipText) {
            content = document.createElement("span");
            content.className = "rcd-tooltip__content";
            content.dataset.rcdTooltipContent = "";
            content.setAttribute("role", "tooltip");
            content.textContent = trigger.dataset.rcdTooltipText;
            generatedContentByTrigger.set(trigger, content);
            generated = true;
        }
        if (!(trigger instanceof HTMLElement) || !(wrapper instanceof HTMLElement) || !(content instanceof HTMLElement)) {
            return false;
        }

        clearPendingOpen();
        clearPendingClose();
        if (active?.trigger === trigger) {
            for (const reason of activationReasons) {
                active.activationReasons.add(reason);
            }
            schedulePosition();
            return true;
        }

        closeTooltip("replace");
        const record = {
            trigger,
            wrapper,
            content,
            parent: content.parentNode,
            nextSibling: content.nextSibling,
            originalTheme: content.getAttribute("data-theme"),
            originalDirection: content.getAttribute("dir"),
            originalDescribedBy: trigger.getAttribute("aria-describedby"),
            generated,
            direction: "ltr",
            activationReasons: new Set(activationReasons)
        };

        if (!(record.parent instanceof Node) && !generated) {
            return false;
        }
        if (generated) {
            record.parent = document.createDocumentFragment();
            record.parent.append(content);
            record.nextSibling = null;
        }
        content.id ||= `rcd-tooltip-${++tooltipSequence}`;
        const describedBy = [record.originalDescribedBy, content.id].filter(Boolean).join(" ");
        trigger.setAttribute("aria-describedby", describedBy);

        active = record;
        wrapper.dataset.state = "open";
        content.dataset.state = "measuring";
        content.setAttribute("data-rcd-tooltip-portal", "");
        syncPortalContext(record);
        document.body.append(content);
        positionActive();

        window.requestAnimationFrame(() => {
            if (active === record) {
                content.dataset.state = "open";
                wrapper.dispatchEvent(new CustomEvent("rcd:tooltip:open", { bubbles: true }));
            }
        });
        return true;
    };

    const queueOpen = (trigger, delay, reason) => {
        clearPendingClose();
        if (active?.trigger === trigger) {
            active.activationReasons.add(reason);
            return;
        }

        const activationReasons = pendingOpen?.trigger === trigger
            ? new Set([...pendingOpen.activationReasons, reason])
            : new Set([reason]);
        clearPendingOpen();
        pendingOpen = {
            trigger,
            activationReasons,
            timer: window.setTimeout(() => {
                const reasons = pendingOpen?.activationReasons || activationReasons;
                pendingOpen = null;
                openTooltip(trigger, reasons);
            }, delay)
        };
    };

    const queueClose = (reason, delay) => {
        clearPendingClose();
        if (pendingOpen) {
            pendingOpen.activationReasons.delete(reason);
            if (pendingOpen.activationReasons.size === 0) {
                clearPendingOpen();
            }
        }

        if (!active) {
            return;
        }

        active.activationReasons.delete(reason);
        if (active.activationReasons.size > 0) {
            return;
        }

        closeTimer = window.setTimeout(() => {
            closeTimer = 0;
            closeTooltip(reason);
        }, delay);
    };

    const relatedIsActive = (relatedTarget) => (
        relatedTarget instanceof Node &&
        Boolean(active && (
            active.wrapper.contains(relatedTarget) ||
            active.content.contains(relatedTarget)
        ))
    );

    document.addEventListener("pointerover", (event) => {
        if (!hoverQuery.matches || !(event.target instanceof Element)) {
            return;
        }

        if (active?.content.contains(event.target)) {
            clearPendingClose();
            active.activationReasons.add("pointer");
            return;
        }

        const trigger = event.target.closest("[data-rcd-tooltip-trigger]");
        if (!(trigger instanceof HTMLElement) || (
            event.relatedTarget instanceof Node &&
            trigger.contains(event.relatedTarget)
        )) {
            return;
        }

        const wrapper = trigger.closest("[data-rcd-tooltip]") || trigger;
        queueOpen(trigger, getDelay(wrapper, "rcdTooltipOpenDelay", 350), "pointer");
    });

    document.addEventListener("pointerout", (event) => {
        if (!hoverQuery.matches || !(event.target instanceof Element)) {
            return;
        }

        if (active?.content.contains(event.target)) {
            if (!relatedIsActive(event.relatedTarget)) {
                queueClose("pointer", getDelay(active.wrapper, "rcdTooltipCloseDelay", 120));
            }
            return;
        }

        const trigger = event.target.closest("[data-rcd-tooltip-trigger]");
        const wrapper = trigger?.closest("[data-rcd-tooltip]") || trigger;
        if (!wrapper || (
            event.relatedTarget instanceof Node &&
            wrapper.contains(event.relatedTarget)
        ) || relatedIsActive(event.relatedTarget)) {
            return;
        }

        queueClose("pointer", getDelay(wrapper, "rcdTooltipCloseDelay", 120));
    });

    document.addEventListener("focusin", (event) => {
        const trigger = resolveTrigger(event.target);
        if (trigger) {
            queueOpen(trigger, 0, "focus");
        }
    });

    document.addEventListener("focusout", (event) => {
        const trigger = resolveTrigger(event.target);
        const wrapper = trigger?.closest("[data-rcd-tooltip]") || trigger;
        if (!wrapper || (
            event.relatedTarget instanceof Node &&
            wrapper.contains(event.relatedTarget)
        )) {
            return;
        }

        queueClose("focus", 0);
    });

    document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && active) {
            closeTooltip("escape");
        }
    });

    document.addEventListener("click", () => {
        closeTooltip("activation");
    }, true);

    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState !== "visible") {
            closeTooltip("visibility");
        }
    });
    window.addEventListener("blur", () => closeTooltip("window-blur"));

    const cleanupWithin = (target) => {
        if (!(target instanceof Element)) {
            return;
        }

        if (pendingOpen && (
            target === pendingOpen.trigger ||
            target.contains(pendingOpen.trigger)
        )) {
            clearPendingOpen();
        }

        if (active && (
            target === active.wrapper ||
            target.contains(active.wrapper) ||
            target === active.trigger ||
            target.contains(active.trigger)
        )) {
            closeTooltip("cleanup");
        }
    };

    document.addEventListener("htmx:beforeSwap", (event) => {
        cleanupWithin(event.detail?.target);
    });
    document.addEventListener("htmx:afterSwap", (event) => {
        prepareTooltipTriggers(event.detail?.target || event.target);
    });
    document.addEventListener("htmx:beforeCleanupElement", (event) => {
        cleanupWithin(event.target);
    });
    window.addEventListener("resize", schedulePosition, { passive: true });
    window.addEventListener("scroll", schedulePosition, { passive: true, capture: true });
    window.visualViewport?.addEventListener("resize", schedulePosition, { passive: true });
    window.visualViewport?.addEventListener("scroll", schedulePosition, { passive: true });

    window.rcdTooltip = {
        open: openTooltip,
        close: closeTooltip,
        refresh: schedulePosition
    };

    return window.rcdTooltip;
};
